export const CHATBOT_DRAG_EDGES = ['top', 'right', 'bottom', 'left'];

export const CHATBOT_EDGE_MARGIN_PX = 24;
export const CHATBOT_MOBILE_EDGE_MARGIN_PX = 16;
export const CHATBOT_MOBILE_MAX_WIDTH_PX = 640;
export const CHATBOT_BUBBLE_SIZE_PX = 56;
export const CHATBOT_PANEL_WIDTH_PX = 520;
export const CHATBOT_PANEL_GAP_PX = 16;
export const CHATBOT_HEADER_HEIGHT_PX = 50;
export const CHATBOT_DRAG_EDGE_STICK_PX = 16;

const HORIZONTAL_EDGES = new Set(['top', 'bottom']);

export const getEdgeMargin = (viewport_width) => (
  viewport_width <= CHATBOT_MOBILE_MAX_WIDTH_PX
    ? CHATBOT_MOBILE_EDGE_MARGIN_PX
    : CHATBOT_EDGE_MARGIN_PX
);

export const getBubbleBounds = ({
  viewport_width,
  viewport_height,
  margin,
  header_height,
  bubble_size,
}) => {
  const left = margin;
  const top = header_height + margin;

  return {
    left,
    right: Math.max(left, viewport_width - margin - bubble_size),
    top,
    bottom: Math.max(top, viewport_height - margin - bubble_size),
  };
};

export const getCenteredOffsetX = ({
  viewport_width,
  margin,
  bubble_size,
}) => margin + (bubble_size / 2) - (viewport_width / 2);

const clampAlongEdge = (value, min, max) => Math.min(max, Math.max(min, value));

export const clampBubblePosition = ({
  edge,
  offset_x,
  offset_y,
  bounds,
}) => {
  const min_x = bounds.left - bounds.right;
  const min_y = bounds.top - bounds.bottom;
  const next_edge = CHATBOT_DRAG_EDGES.includes(edge) ? edge : 'bottom';
  let x = Number.isFinite(offset_x) ? offset_x : 0;
  let y = Number.isFinite(offset_y) ? offset_y : 0;

  if (next_edge === 'left') {
    x = min_x;
    y = clampAlongEdge(y, min_y, 0);
  } else if (next_edge === 'right') {
    x = 0;
    y = clampAlongEdge(y, min_y, 0);
  } else if (next_edge === 'top') {
    y = min_y;
    x = clampAlongEdge(x, min_x, 0);
  } else {
    y = 0;
    x = clampAlongEdge(x, min_x, 0);
  }

  return {
    edge: next_edge,
    offset_x: x,
    offset_y: y,
  };
};

const ADJACENT_EDGES = {
  top: ['top', 'left', 'right'],
  right: ['right', 'top', 'bottom'],
  bottom: ['bottom', 'left', 'right'],
  left: ['left', 'top', 'bottom'],
};

export const projectBubbleOntoPerimeter = ({
  center_x,
  center_y,
  current_edge,
  bounds,
  bubble_size,
  stick_px = CHATBOT_DRAG_EDGE_STICK_PX,
}) => {
  const half = bubble_size / 2;
  const left = bounds.left + half;
  const right = bounds.right + half;
  const top = bounds.top + half;
  const bottom = bounds.bottom + half;
  const x = clampAlongEdge(center_x, left, right);
  const y = clampAlongEdge(center_y, top, bottom);
  const candidates = [
    {
      edge: 'left',
      center_x: left,
      center_y: y,
    },
    {
      edge: 'right',
      center_x: right,
      center_y: y,
    },
    {
      edge: 'top',
      center_x: x,
      center_y: top,
    },
    {
      edge: 'bottom',
      center_x: x,
      center_y: bottom,
    },
  ];
  const allowed_edges = ADJACENT_EDGES[current_edge] || CHATBOT_DRAG_EDGES;
  const available_candidates = candidates.filter(
    (candidate) => allowed_edges.includes(candidate.edge)
  );
  let best = available_candidates[0];
  let best_distance = Infinity;

  available_candidates.forEach((candidate) => {
    const delta_x = candidate.center_x - center_x;
    const delta_y = candidate.center_y - center_y;
    let distance = Math.hypot(delta_x, delta_y);

    if (candidate.edge === current_edge) {
      distance -= stick_px;
    }

    if (distance < best_distance) {
      best = candidate;
      best_distance = distance;
    }
  });

  return clampBubblePosition({
    edge: best.edge,
    offset_x: (best.center_x - half) - bounds.right,
    offset_y: (best.center_y - half) - bounds.bottom,
    bounds,
  });
};

const approachProgress = (distance, lane) => {
  if (distance >= lane) {
    return 0;
  }

  if (distance <= 0) {
    return 1;
  }

  return (lane - distance) / lane;
};

const clampPanelBox = (box, min_start, max_end) => {
  let start = box.start;
  let end = box.end;

  if (start < min_start) {
    start = min_start;
  }

  if (end > max_end) {
    end = max_end;
  }

  if (end < start) {
    end = start;
  }

  return {
    start,
    end,
  };
};

/**
 * While the bubble is dragged toward a corner, ease the open panel off that
 * corner so the perpendicular border stays clear. At the corner the parked
 * panel matches the layout of the edge the bubble is about to enter.
 */
const applyCornerLaneClearance = ({
  panel,
  edge,
  bounds,
  bubble_left,
  bubble_top,
  bubble_size,
  panel_gap,
  margin,
  header_height,
  viewport_width,
  viewport_height,
}) => {
  const lane = bubble_size + panel_gap;
  const distance_to_left = bubble_left - bounds.left;
  const distance_to_right = bounds.right - bubble_left;
  const distance_to_top = bubble_top - bounds.top;
  const distance_to_bottom = bounds.bottom - bubble_top;
  let left = panel.left;
  let right = panel.left + panel.width;
  let top = panel.top;
  let bottom = panel.top + panel.height;

  if (HORIZONTAL_EDGES.has(edge)) {
    const right_progress = approachProgress(distance_to_right, lane);
    const parked_right = bounds.right - panel_gap;

    if (right_progress > 0 && right > parked_right) {
      const target_right = right + ((parked_right - right) * right_progress);

      left += target_right - right;
      right = target_right;
    }

    const left_progress = approachProgress(distance_to_left, lane);
    const parked_left = bounds.left + bubble_size + panel_gap;

    if (left_progress > 0 && left < parked_left) {
      const target_left = left + ((parked_left - left) * left_progress);

      right += target_left - left;
      left = target_left;
    }
  } else {
    const bottom_progress = approachProgress(distance_to_bottom, lane);
    const parked_bottom = bounds.bottom - panel_gap;

    if (bottom_progress > 0 && bottom > parked_bottom) {
      const target_bottom = bottom + ((parked_bottom - bottom) * bottom_progress);

      top += target_bottom - bottom;
      bottom = target_bottom;
    }

    const top_progress = approachProgress(distance_to_top, lane);
    const parked_top = bounds.top + bubble_size + panel_gap;

    if (top_progress > 0 && top < parked_top) {
      const target_top = top + ((parked_top - top) * top_progress);

      bottom += target_top - top;
      top = target_top;
    }
  }

  const horizontal = clampPanelBox(
    {
      start: left,
      end: right,
    },
    margin,
    viewport_width - margin
  );
  const vertical = clampPanelBox(
    {
      start: top,
      end: bottom,
    },
    header_height + margin,
    viewport_height - margin
  );

  return {
    left: horizontal.start,
    top: vertical.start,
    width: horizontal.end - horizontal.start,
    height: vertical.end - vertical.start,
  };
};

const alignPanelToBubble = ({
  bubble_right,
  panel_width,
  margin,
  viewport_width,
}) => {
  const max_left = Math.max(margin, viewport_width - margin - panel_width);
  let panel_left = bubble_right - panel_width;

  if (panel_left < margin) {
    panel_left = margin;
  }

  if (panel_left > max_left) {
    panel_left = max_left;
  }

  return panel_left;
};

export const getOpenPanelRect = ({
  edge,
  offset_x,
  offset_y,
  bounds,
  viewport_width,
  viewport_height,
  margin,
  header_height,
  bubble_size,
  panel_max_width,
  panel_gap,
  reserve_corner_lanes = false,
}) => {
  const bubble_left = bounds.right + offset_x;
  const bubble_top = bounds.bottom + offset_y;
  const bubble_right = bubble_left + bubble_size;
  const bubble_bottom = bubble_top + bubble_size;
  const is_vertical = edge === 'left' || edge === 'right';
  const lane = is_vertical ? bubble_size + panel_gap : 0;
  const available_width = viewport_width - (margin * 2) - lane;
  const panel_width = Math.max(0, Math.min(panel_max_width, available_width));
  let panel_left;
  let panel_top;
  let panel_height;

  if (edge === 'right') {
    panel_left = bubble_left - panel_gap - panel_width;
    panel_top = header_height + margin;
    panel_height = Math.max(0, viewport_height - margin - panel_top);
  } else if (edge === 'left') {
    panel_left = bubble_right + panel_gap;
    panel_top = header_height + margin;
    panel_height = Math.max(0, viewport_height - margin - panel_top);
  } else if (edge === 'top') {
    panel_left = alignPanelToBubble({
      bubble_right,
      panel_width,
      margin,
      viewport_width,
    });
    panel_top = bubble_bottom + panel_gap;
    panel_height = Math.max(0, viewport_height - margin - panel_top);
  } else {
    panel_left = alignPanelToBubble({
      bubble_right,
      panel_width,
      margin,
      viewport_width,
    });
    panel_top = header_height + margin;
    panel_height = Math.max(0, bubble_top - panel_gap - panel_top);
  }

  const base_panel = {
    left: panel_left,
    top: panel_top,
    width: panel_width,
    height: panel_height,
  };

  if (!reserve_corner_lanes) {
    return base_panel;
  }

  return applyCornerLaneClearance({
    panel: base_panel,
    edge,
    bounds,
    bubble_left,
    bubble_top,
    bubble_size,
    panel_gap,
    margin,
    header_height,
    viewport_width,
    viewport_height,
  });
};
